import { useBrands } from '../../../context/BrandsContext';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

export default function ProductPage() {
  const { brand: brandSlug, productId } = useParams<{ brand: string; productId: string }>();
  const navigate = useNavigate();
  const { brands } = useBrands();

  const brand = brandSlug ? brands.find(b => b.slug === brandSlug) : undefined;
  const product = brand?.products.find(p => p.id === productId);
  const related = brand?.products.filter(p => p.id !== productId).slice(0, 3) ?? [];

  const imgs = product?.images && product.images.length > 1 ? product.images : product ? [product.image] : [];
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImg(0);
  }, [productId]);

  const prevImg = () => setActiveImg(i => (i - 1 + imgs.length) % imgs.length);
  const nextImg = () => setActiveImg(i => (i + 1) % imgs.length);

  useEffect(() => {
    if (imgs.length <= 1) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prevImg();
      if (e.key === 'ArrowRight') nextImg();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [imgs.length]);

  const handleOrder = () => {
    navigate(`/order?brand=${brandSlug}&product=${productId}&productName=${encodeURIComponent(product?.name ?? '')}`);
  };

  if (!brand || !product) {
    return (
      <div className="bg-white min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-black text-2xl font-black mb-4">Product not found</div>
            <button
              onClick={() => navigate('/shop')}
              className="bg-black text-white px-8 py-3 font-bold hover:bg-gray-800 transition-all cursor-pointer"
            >
              Back to Shop
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <Navbar />

      {/* Breadcrumb bar */}
      <div className="bg-black pt-24 md:pt-[calc(2.5rem+5rem)] pb-0">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-5">
          <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider">
            <button onClick={() => navigate('/')} className="hover:text-white transition-colors cursor-pointer">Home</button>
            <i className="ri-arrow-right-s-line text-gray-600"></i>
            <button onClick={() => navigate('/shop')} className="hover:text-white transition-colors cursor-pointer">Shop</button>
            <i className="ri-arrow-right-s-line text-gray-600"></i>
            <button onClick={() => navigate(`/shop/${brand.slug}`)} className="hover:text-white transition-colors cursor-pointer">{brand.name}</button>
            <i className="ri-arrow-right-s-line text-gray-600"></i>
            <span className="text-white">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Product Detail */}
      <section className="bg-white py-10 md:py-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-20 items-start">

            {/* Left — Image / Carousel */}
            <div className="flex flex-col gap-3">
              {/* Main image */}
              <div className="relative bg-gray-50 overflow-hidden group">
                <img
                  src={imgs[activeImg]}
                  alt={product.name}
                  className="w-full h-[380px] md:h-[560px] object-cover transition-opacity duration-300"
                />
                <div className="absolute top-6 left-6">
                  <span className="bg-black text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5">
                    {product.category}
                  </span>
                </div>
                {/* Corner accents */}
                <div className="absolute top-0 right-0 w-0 h-0 border-t-[50px] border-t-black border-l-[50px] border-l-transparent"></div>
                <div className="absolute bottom-0 left-0 w-0 h-0 border-b-[50px] border-b-black border-r-[50px] border-r-transparent"></div>

                {/* Arrow nav — only when multiple images */}
                {imgs.length > 1 && (
                  <>
                    <button
                      onClick={prevImg}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/70 hover:bg-black text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line text-xl"></i>
                    </button>
                    <button
                      onClick={nextImg}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/70 hover:bg-black text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line text-xl"></i>
                    </button>
                    {/* Counter */}
                    <div className="absolute bottom-4 right-4 bg-black/70 text-white text-xs font-bold px-2.5 py-1 tracking-wider">
                      {activeImg + 1} / {imgs.length}
                    </div>
                  </>
                )}
              </div>

              {/* Thumbnails strip — only when multiple images */}
              {imgs.length > 1 && (
                <div className="flex gap-2 overflow-x-auto">
                  {imgs.map((src, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      className={`flex-shrink-0 w-20 h-20 overflow-hidden border-2 transition-all cursor-pointer ${
                        i === activeImg ? 'border-black' : 'border-transparent opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img src={src} alt={`${product.name} view ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right — Details */}
            <div className="flex flex-col gap-7 lg:pt-2">

              {/* Brand + Name */}
              <div>
                <div className="text-gray-400 text-xs uppercase tracking-[4px] font-bold mb-3">{brand.fullName}</div>
                <h1 className="text-black text-4xl md:text-5xl font-black tracking-tighter leading-tight mb-4">
                  {product.name.toUpperCase()}
                </h1>
                <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                  {product.description}
                </p>
              </div>

              {/* Price */}
              <div className="bg-gray-50 px-6 py-5 border-l-4 border-black">
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Price</div>
                <div className="text-4xl font-black text-black">{product.price}</div>
                {product.priceNote && (
                  <div className="text-sm text-gray-400 mt-1">{product.priceNote}</div>
                )}
              </div>

              {/* Features */}
              <div>
                <h3 className="text-black text-xs uppercase tracking-[3px] font-bold mb-4">Key Features</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {product.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-black text-base flex-shrink-0"></i>
                      {f}
                    </div>
                  ))}
                </div>
              </div>

              {/* Compatible Models */}
              {product.compatibleModels && product.compatibleModels.length > 0 && (
                <div>
                  <h3 className="text-black text-xs uppercase tracking-[3px] font-bold mb-3">Compatible Models</h3>
                  <div className="flex flex-wrap gap-2">
                    {product.compatibleModels.map((model, i) => (
                      <span key={i} className="bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1.5 border-l-2 border-black">
                        {model}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  onClick={handleOrder}
                  className="flex-1 bg-black text-white py-4 px-8 text-sm font-bold uppercase tracking-wider hover:bg-gray-800 transition-all flex items-center justify-center gap-3 cursor-pointer group"
                >
                  Order Now
                  <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform"></i>
                </button>
                <a
                  href="https://wa.me/381642428074"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 border-2 border-black text-black py-4 px-6 text-sm font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-all cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                  Ask on WhatsApp
                </a>
              </div>

              {/* Back link */}
              <button
                onClick={() => navigate(`/shop/${brand.slug}`)}
                className="flex items-center gap-2 text-gray-400 hover:text-black transition-colors text-sm font-medium cursor-pointer self-start"
              >
                <i className="ri-arrow-left-line"></i>
                Back to {brand.name}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      {related.length > 0 && (
        <section className="bg-gray-50 py-12 md:py-20 border-t border-gray-100">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
            <div className="flex items-center gap-4 mb-8 md:mb-10">
              <div className="w-8 h-[2px] bg-black"></div>
              <h2 className="text-black text-2xl md:text-3xl font-black tracking-tighter uppercase">
                More from {brand.name}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <div
                  key={p.id}
                  onClick={() => navigate(`/shop/${brand.slug}/${p.id}`)}
                  className="group bg-white border border-gray-200 hover:border-black transition-all cursor-pointer"
                >
                  <div className="relative overflow-hidden h-52 bg-gray-50">
                    <img
                      src={p.image}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-black text-white text-xs font-bold uppercase tracking-wider px-2.5 py-1">
                        {p.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-black font-black uppercase tracking-tight mb-1">{p.name}</h3>
                    <div className="text-xl font-black text-black">{p.price}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
